import { Button } from "components/Button/Button"
import { Space } from "components/Space/Space"
import { Tag } from "components/Tag/Tag"
import { DatePicker } from "components/DatePicker/DatePicker"
import { Disclosure } from "components/Disclosure/Disclosure"
import { Empty } from "components/Empty/Empty"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Spinner } from "components/Spinner/Spinner"
import { Tooltip } from "components/Tooltip/Tooltip"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { useListSort } from "hooks/useListSort"
import { NoDataIcon } from "icons/NoDataIcon"
import { PatientUtilization, PatientUtilizationCode } from "interface/billing"
import { ComponentProps, useMemo, useRef } from "react"
import { useSearchParams } from "react-router"
import { usePatientsUtilization } from "services/billings"
import { cn } from "styles/cn"
import { asDate, Dayjs, today } from "utils/date"

export const Billing = () => {
	const targetScroll = useRef<HTMLUListElement>(null)
	const { sort, toggle, sortIcon, labelSort } = useListSort<PatientUtilization>(
		["fullName-ascending", "medicalRecordNumber-ascending"],
	)
	const previousMonth = today().subtract(1, "month")
	const previousMonthAsString = previousMonth.format("YYYY-MM")
	const defaultSearchParams = useMemo(
		() => ({
			month: previousMonthAsString,
			query: "",
		}),
		[previousMonthAsString],
	)
	const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams)
	const month = searchParams.get("month") ?? previousMonthAsString
	const monthAsDate = useMemo(() => asDate(month), [month])
	const query = searchParams.get("query") ?? ""
	const {
		data: { pages: patients = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
		isFetched,
	} = usePatientsUtilization({ month, sort, query })
	const isLoading = isFetching || isFetchingNextPage

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<Spinner spinning={isLoading}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col gap-4 overflow-hidden">
				<h2
					className="flex flex-wrap items-center justify-between gap-2 font-nunito text-3xl font-semibold"
					aria-label="Billing section"
				>
					Billing
					<DatePicker.Month
						size="large"
						value={monthAsDate}
						maxDate={previousMonth}
						className="max-w-fit min-w-48"
						aria-label="Select month"
						onChange={(date) => {
							if (!date) return
							setSearchParams({
								...Object.fromEntries(searchParams.entries()),
								month: `${date.format("YYYY-MM")}`,
							})
						}}
						cellRender={(current, info) => {
							const currentDate = current as Dayjs
							if (currentDate.isAfter(previousMonth)) {
								return (
									<Tooltip.Dark
										placement="top"
										title={`This report will be available at ${currentDate.add(1, "month").startOf("month").format("Do MMMM YYYY")}`}
									>
										{info.originNode}
									</Tooltip.Dark>
								)
							}
							return info.originNode
						}}
					/>
				</h2>

				<p>
					The information below is provided as a summary of patient utilization
					of Empo Health's services. The customer is responsible for making
					final determination of codes to bill and submitting claims properly.
				</p>

				<h3 className="font-nunito text-2xl font-semibold">
					Patient utilization
				</h3>

				<SearchInput
					value={query}
					placeholder="Search patients"
					onSearch={(query) => setSearchParams({ query })}
				/>

				{patients.length > 1 && (
					<Space.Compact className="justify-end @4xl:hidden">
						<Button
							icon={sortIcon("medicalRecordNumber")}
							aria-label={labelSort(
								"medicalRecordNumber",
								"Medical record number",
							)}
							onClick={() => toggle("medicalRecordNumber")}
						>
							Medical record number
						</Button>
						<Button
							icon={sortIcon("fullName")}
							aria-label={labelSort("fullName", "Full name")}
							onClick={() => toggle("fullName")}
						>
							Full name
						</Button>
					</Space.Compact>
				)}

				<div className="flex flex-col gap-4 overflow-hidden">
					<div className="hidden gap-4 rounded-lg bg-mint-0 p-5 py-3 @4xl:flex">
						<div className="grid w-full grid-cols-[200px_repeat(7,1fr)_auto] items-start gap-2 font-bold">
							<div
								role="columnheader"
								className="flex gap-2 whitespace-break-spaces"
							>
								<>
									Medical record number
									{patients.length > 1 && (
										<Button
											size="small"
											type="dashed"
											icon={sortIcon("medicalRecordNumber")}
											aria-label={labelSort(
												"medicalRecordNumber",
												"Medical record number",
											)}
											onClick={() => toggle("medicalRecordNumber")}
										/>
									)}
								</>
							</div>
							<div
								role="columnheader"
								className="flex gap-2 whitespace-break-spaces"
							>
								<>
									Full name
									{patients.length > 1 && (
										<Button
											size="small"
											type="dashed"
											icon={sortIcon("fullName")}
											aria-label={labelSort("fullName", "Full name")}
											onClick={() => toggle("fullName")}
										/>
									)}
								</>
							</div>
							<div role="columnheader" className="whitespace-break-spaces">
								Installed this month?
							</div>
							<div role="columnheader" className="whitespace-break-spaces">
								Days of usage
							</div>
							<div role="columnheader" className="whitespace-break-spaces">
								Total monitoring time
							</div>
							<div role="columnheader" className="whitespace-break-spaces">
								Live interaction?
							</div>
							<div role="columnheader" className="whitespace-break-spaces">
								Data codes
							</div>
							<div role="columnheader" className="whitespace-break-spaces">
								Monitoring codes
							</div>
							<div className="invisible size-3.5" />
						</div>
					</div>

					<ul
						ref={targetScroll}
						className="flex flex-col gap-4 overflow-y-auto"
						aria-label={
							isLoading
								? "Waiting for patient utilization list"
								: `Patient utilization list with ${patients.length} patient(s)`
						}
					>
						{patients.map((patient) => {
							const {
								patientId,
								medicalRecordNumber,
								fullName,
								footprintInstallationDate,
								monitoringInteractionTime,
								isLiveInteraction,
								dataCollectionCodes,
								monitoringInteractionCodes,
							} = patient
							const details = buildPatientDetails(patient, month)

							return (
								<li
									key={patientId}
									aria-label={`Utilization for patient ${fullName}`}
								>
									<Disclosure
										name="patient utilization"
										summary={
											<div className="flex w-full flex-col items-center gap-2 @4xl:grid @4xl:grid-cols-[200px_repeat(7,1fr)]">
												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Medical record number:
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														{medicalRecordNumber}
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Patient full name:
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														{fullName}
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Installed this month?
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														<InstalledThisMonth
															footprintInstallationDate={
																footprintInstallationDate
															}
															month={month}
														/>
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Days of usage
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														<DaysOfUsage patient={patient} />
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Total monitoring time
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														{monitoringInteractionTime}
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Live interaction?
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														<Tag className="mr-0">{isLiveInteraction}</Tag>
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Data codes
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														<CodesSummary<"99453" | "99454" | "99445">
															codes={dataCollectionCodes}
														/>
													</span>
												</div>

												<div className="flex w-full justify-between gap-2 wrap-anywhere">
													<span className="text-navy-60 @4xl:hidden">
														Monitoring codes
													</span>
													<span className="text-navy-100 @4xl:text-navy-60">
														<CodesSummary<"99457" | "99458" | "99470">
															codes={monitoringInteractionCodes}
														/>
													</span>
												</div>
											</div>
										}
									>
										<dl
											className="grid grid-cols-2 gap-2 text-navy-100"
											aria-label={`Patient details of ${fullName}`}
										>
											{details.map(([title, description]) => (
												<dt
													key={title as string}
													className="flex items-start gap-2"
												>
													<span className="text-nowrap text-navy-60">
														{title}:
													</span>
													{description}
												</dt>
											))}
										</dl>
									</Disclosure>
								</li>
							)
						})}

						{patients.length === 0 && isFetched && (
							<Empty
								Icon={NoDataIcon}
								description={`No patient utilization in ${monthAsDate.format("MMMM YYYY")}`}
							/>
						)}
					</ul>
				</div>
			</div>
		</Spinner>
	)
}

const buildPatientDetails = (patient: PatientUtilization, month: string) => [
	["Medical record number", patient.medicalRecordNumber],
	["Full name", patient.fullName],
	[
		"Installed this month?",
		<InstalledThisMonth
			footprintInstallationDate={patient.footprintInstallationDate}
			month={month}
			showWhen
		/>,
	],
	["Days of usage", <DaysOfUsage patient={patient} expanded />],
	["Total monitoring time", patient.monitoringInteractionTime],
	[
		"Live interaction?",
		<Tag className="mr-0">{patient.isLiveInteraction}</Tag>,
	],
	[
		"Data collection codes",
		<CodesExpanded<"99453" | "99454" | "99445">
			codes={patient.dataCollectionCodes}
		/>,
	],
	[
		"Monitoring/interaction codes",
		<CodesExpanded<"99457" | "99458" | "99470">
			codes={patient.monitoringInteractionCodes}
		/>,
	],
]

function InstalledThisMonth({
	footprintInstallationDate,
	month,
	showWhen = false,
}: {
	footprintInstallationDate: PatientUtilization["footprintInstallationDate"]
	month: string
	showWhen?: boolean
}) {
	const date = asDate(footprintInstallationDate)

	return (
		<div className="flex items-center gap-2">
			<Tag className="mr-0 w-fit">
				{date.format("YYYY-MM") === month ? "Yes" : "No"}
			</Tag>
			{showWhen ? date.format("MMM D") : null}
		</div>
	)
}

function DaysOfUsage({
	patient,
	expanded = false,
}: {
	patient: PatientUtilization
	expanded?: boolean
}) {
	if (!expanded) {
		return patient.daysOfFootprintUsage.reduce(
			(acc, { quantity }) => acc + quantity,
			0,
		)
	}

	return (
		<ul>
			{patient.daysOfFootprintUsage
				.toSorted((a, b) => asDate(a.from).unix() - asDate(b.from).unix())
				.map(({ from, to, quantity }) => (
					<li key={`${patient.patientId}-${from}-${to}`}>
						{`${asDate(from).format("MMM D")} – ${asDate(to).format("MMM D")}: ${quantity} days`}
					</li>
				))}
		</ul>
	)
}

function CodesSummary<T extends string>({
	codes,
}: {
	codes: PatientUtilizationCode<T>[]
}) {
	const uniqueCodes = codes
		.flatMap(({ codeInfos }) => codeInfos.map(({ code }) => code))
		.filter((value, index, self) => self.indexOf(value) === index)

	return uniqueCodes.join(", ")
}

function CodesExpanded<T extends string>({
	codes,
	...props
}: ComponentProps<"ul"> & {
	codes: PatientUtilizationCode<T>[]
}) {
	return (
		<ul {...props} className={cn("flex flex-col divide-y", props.className)}>
			{codes
				.toSorted(
					(a, b) =>
						asDate(a.dateOfService).unix() - asDate(b.dateOfService).unix(),
				)
				.map(({ codeInfos, dateOfService }) => {
					// Sum quantities for each code
					const codeQuantities = codeInfos.reduce(
						(acc, { code, quantity }) => {
							acc[code] = (acc[code] || 0) + quantity
							return acc
						},
						{} as Record<T, number>,
					)

					return (
						<li key={dateOfService} className="flex flex-col border-dashed">
							<span className="flex gap-1">
								Date of service: <b>{asDate(dateOfService).format("MMM D")}</b>
							</span>
							<span>{`Place of service: ${codeInfos[0].placeOfService}`}</span>
							<span className="flex gap-1 font-semibold">
								{Object.entries(codeQuantities)
									.map(([code, totalQuantity]) => `${code} (x${totalQuantity})`)
									.join(", ")}
							</span>
						</li>
					)
				})}
		</ul>
	)
}
